import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, AlertTriangle, CheckCircle2 } from "lucide-react";
import Avatar from "../Avatar";
import { forgotPassword } from "@/lib/api";

// Forgot-password screen. Reachable from the "forgot password?" link on
// the sign-in gate. Sends a reset link to the email the account signs in with.
// Backend ALWAYS returns 200 with a generic body shape so unauthenticated
// callers can't enumerate emails. When the caller is signed in and
// requesting a reset for their OWN account, the backend includes extra
// `delivery_status` + `diagnostic` + `recipient_hint` fields — we surface
// those here so the owner can actually debug a stuck delivery.
export default function ForgotPasswordScreen({ onBack }) {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [deliveryConfigured, setDeliveryConfigured] = useState(true);
    const [diagnostic, setDiagnostic] = useState(null); // {status, message, recipient}

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            const res = await forgotPassword(email.trim().toLowerCase());
            setDeliveryConfigured(res?.email_delivery_configured ?? true);
            if (res?.delivery_status) {
                setDiagnostic({
                    status: res.delivery_status,
                    message: res.diagnostic || null,
                    recipient: res.recipient_hint || null,
                });
            }
            setSent(true);
        } catch {
            // Even on error we show the same generic success — keeps the
            // anti-enumeration contract.
            setSent(true);
        }
        setBusy(false);
    };

    const inputCls = "w-full bg-cream rounded-2xl px-4 py-3 font-body text-sm text-warm-900 outline-none border-2 border-transparent focus:border-terracotta transition-all";

    // Diagnostic banner ONLY appears when backend included it (= caller is
    // signed-in as the owner of this email). Otherwise the same generic
    // success copy renders regardless of the actual delivery outcome.
    const renderDiagnostic = () => {
        if (!diagnostic) return null;
        const { status, message, recipient } = diagnostic;
        if (status === "sent") {
            return (
                <div className="flex items-start gap-2 rounded-2xl bg-sage-soft/40 border border-sage/30 px-3 py-2.5"
                     data-testid="forgot-password-diagnostic-sent">
                    <CheckCircle2 size={14} className="text-sage shrink-0 mt-0.5" />
                    <p className="font-body text-xs text-warm-800 leading-relaxed">
                        sent to <span className="font-semibold">{recipient}</span> ✓ check inbox + spam.
                    </p>
                </div>
            );
        }
        // Any non-sent status: surface the real reason.
        return (
            <div className="flex items-start gap-2 rounded-2xl bg-terracotta-soft/40 border border-terracotta/40 px-3 py-2.5"
                 data-testid="forgot-password-diagnostic-error">
                <AlertTriangle size={14} className="text-terracotta shrink-0 mt-0.5" />
                <div className="space-y-1">
                    <p className="font-body text-xs text-warm-900 leading-relaxed font-semibold">
                        the email didn't go out 🥺
                    </p>
                    {message && (
                        <p className="font-body text-[11px] text-warm-700 leading-relaxed">
                            {message}
                        </p>
                    )}
                    {recipient && (
                        <p className="font-body text-[11px] text-warm-500 leading-relaxed">
                            recipient on file: <span className="font-semibold">{recipient}</span>
                        </p>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div
            className="App font-body min-h-screen [min-height:100dvh] flex items-center justify-center px-5 py-10"
            data-testid="forgot-password-screen"
        >
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="w-full max-w-sm"
            >
                <div className="flex flex-col items-center mb-5">
                    <Avatar mood={sent ? "cozy" : "worried"} size={120} />
                    <h1 className="font-display text-3xl text-warm-900 mt-2">
                        {sent ? "check your email 🍡" : "forgot it? happens 🌸"}
                    </h1>
                </div>

                <div className="bg-surface rounded-clay shadow-clay p-5">
                    {sent ? (
                        <div className="space-y-3" data-testid="forgot-password-sent">
                            <p className="font-body text-sm text-warm-800 leading-relaxed">
                                {deliveryConfigured
                                    ? "if there's an account with this email, mochi just sent a reset link. the link lasts 30 minutes."
                                    : "mochi tried, but couldn't send right now. try again in a minute, or contact support."}
                            </p>
                            {renderDiagnostic()}
                            <p className="font-body text-xs text-warm-500 leading-relaxed">
                                didn't get it? check spam, or make sure the email was right.
                            </p>
                            <button
                                type="button"
                                onClick={onBack}
                                className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover text-cream font-display text-sm py-3 transition-all"
                                data-testid="forgot-password-back-to-signin"
                            >
                                back to sign in
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-3" data-testid="forgot-password-form">
                            <p className="font-body text-sm text-warm-800 leading-relaxed">
                                type the email you sign in with and mochi will send a reset link there.
                            </p>
                            <input
                                type="email"
                                required
                                maxLength={254}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your email"
                                autoCapitalize="none"
                                autoCorrect="off"
                                autoComplete="email"
                                inputMode="email"
                                className={inputCls}
                                data-testid="forgot-password-email-input"
                            />
                            <button
                                type="submit"
                                disabled={busy}
                                className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover text-cream font-display text-base py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-all active:scale-[0.98]"
                                data-testid="forgot-password-submit-btn"
                            >
                                <Mail size={16} /> {busy ? "sending…" : "send reset link"}
                            </button>
                            <button
                                type="button"
                                onClick={onBack}
                                className="w-full inline-flex items-center justify-center gap-1 font-body text-xs text-warm-500 hover:text-warm-900 transition-colors"
                                data-testid="forgot-password-back-btn"
                            >
                                <ArrowLeft size={12} /> back to sign in
                            </button>
                        </form>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
